import { AlertTriangle, CheckCircle, CircleAlert, Info, X } from "lucide-react";
import { useUiStore, type Toast, type ToastType } from "../store/uiStore";

const SOLID: Record<ToastType, { bg: string; Icon: typeof Info }> = {
  success: { bg: "bg-success", Icon: CheckCircle },
  error: { bg: "bg-danger", Icon: CircleAlert },
  warning: { bg: "bg-warning", Icon: AlertTriangle },
  info: { bg: "bg-info", Icon: Info },
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { bg, Icon } = SOLID[toast.type];
  const hasActions = (toast.actions?.length ?? 0) > 0;

  return (
    <div role="status" className={`relative overflow-hidden rounded-xl text-sm text-white shadow-lg ${bg} ${toast.exiting ? "toast-exit" : "toast-enter"}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <Icon size={22} className="shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">{toast.message}</span>
        {hasActions && (
          <div className="flex shrink-0 flex-wrap gap-2" role="group" aria-label="Toast actions">
            {toast.actions?.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => {
                  onDismiss(toast.id);
                  action.onClick?.();
                }}
                className={`cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/60 ${action.primary ? "bg-white text-gray-800 hover:bg-white/90" : "border border-white/40 text-white hover:bg-white/10"}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        {!hasActions && toast.dismissable && (
          <button type="button" onClick={() => onDismiss(toast.id)} aria-label="Dismiss notification" className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60">
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Top-right toast stack fed by uiStore. */
export function ToastContainer() {
  const toasts = useUiStore((state) => state.toasts);
  const dismissToast = useUiStore((state) => state.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 top-20 z-[60] flex w-full max-w-md flex-col gap-2 px-3 sm:px-0" aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
